import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import TopBar from "../components/TopBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = "#39ff14";

const STATUSES = [
  { key: "new", label_es: "Nueva", label_en: "New", color: "border-neutral-500/40 text-neutral-300 bg-neutral-500/10" },
  { key: "under_review", label_es: "En revisión", label_en: "Under review", color: "border-sky-500/40 text-sky-300 bg-sky-500/10" },
  { key: "evaluation_invited", label_es: "Invitada a evaluación", label_en: "Evaluation invited", color: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  { key: "accepted", label_es: "Aceptada", label_en: "Accepted", color: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" },
  { key: "waitlist", label_es: "Lista de espera", label_en: "Waitlist", color: "border-purple-500/40 text-purple-300 bg-purple-500/10" },
  { key: "not_selected", label_es: "No seleccionada", label_en: "Not selected", color: "border-[#e05555]/40 text-[#e05555] bg-[#e05555]/10" },
];

export default function AdminElitePage() {
  const navigate = useNavigate();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", position: "", club: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.status) q.set("status", filter.status);
      if (filter.position) q.set("position", filter.position);
      if (filter.club) q.set("club", filter.club);
      const r = await axios.get(`${API}/admin/elite/applications?${q}`, { headers });
      setApps(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [filter, navigate]);

  useEffect(() => {
    if (!password) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate, password]);

  const updateApp = async (id, patch) => {
    const r = await axios.patch(`${API}/admin/elite/applications/${id}`, patch, { headers });
    setApps((a) => a.map((x) => (x.id === id ? r.data : x)));
    if (selected?.id === id) setSelected(r.data);
  };

  const removeApp = async (id) => {
    if (!window.confirm("¿Eliminar esta aplicación?")) return;
    await axios.delete(`${API}/admin/elite/applications/${id}`, { headers });
    setSelected(null);
    await load();
  };

  const convertApp = async (app) => {
    if (!window.confirm(`¿Convertir a ${app.player_name} en jugador registrado?`)) return;
    const r = await axios.post(`${API}/admin/elite/applications/${app.id}/convert`, {}, { headers });
    alert(`Jugador creado. Número #${r.data.player_number || "—"}. Token portal: ${r.data.access_token?.slice(0, 8) || "—"}...`);
    await load();
  };

  const stats = STATUSES.map((s) => ({ ...s, count: apps.filter((a) => a.status === s.key).length }));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/admin" className="text-sm text-neutral-400 hover:opacity-80 mb-4 inline-block" style={{ color: GREEN }}>
          ← Admin
        </Link>

        <div className="rounded-2xl border p-5 sm:p-8 mb-6" style={{ borderColor: `${GREEN}30`, background: `linear-gradient(135deg, #0a0a0a, ${GREEN}08)` }}>
          <div className="text-[10px] uppercase tracking-[0.4em] mb-2" style={{ color: GREEN }}>SOCCERTROOP ELITE</div>
          <h1 className="font-bebas text-4xl sm:text-5xl tracking-wider">Aplicaciones Elite</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Revisa cada aplicación, cambia el estado, agrega notas internas y convierte a las jugadoras aceptadas en perfiles reales del sistema SoccerTroop.
          </p>
        </div>

        {/* Status counters */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-5">
          <button
            onClick={() => setFilter((f) => ({ ...f, status: "" }))}
            data-testid="elite-filter-all"
            className={`rounded-xl border p-3 text-center transition-colors ${filter.status === "" ? "border-white" : "border-[#1a1a1a] hover:border-neutral-600"}`}
          >
            <div className="font-bebas text-2xl">{apps.length}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total</div>
          </button>
          {stats.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter((f) => ({ ...f, status: filter.status === s.key ? "" : s.key }))}
              data-testid={`elite-filter-${s.key}`}
              className={`rounded-xl border p-3 text-center transition-colors ${filter.status === s.key ? s.color : "border-[#1a1a1a] hover:border-neutral-600"}`}
            >
              <div className="font-bebas text-2xl">{s.count}</div>
              <div className="text-[10px] uppercase tracking-widest">{s.label_es}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
          <input
            placeholder="Filtrar por posición"
            value={filter.position}
            onChange={(e) => setFilter((f) => ({ ...f, position: e.target.value }))}
            data-testid="elite-filter-position"
            className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg px-3 py-2 text-sm focus:outline-none"
          />
          <input
            placeholder="Filtrar por club"
            value={filter.club}
            onChange={(e) => setFilter((f) => ({ ...f, club: e.target.value }))}
            data-testid="elite-filter-club"
            className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg px-3 py-2 text-sm focus:outline-none"
          />
          <button
            onClick={() => setFilter({ status: "", position: "", club: "" })}
            className="rounded-lg border border-[#1a1a1a] px-3 py-2 text-sm hover:border-neutral-500 text-neutral-300"
          >
            Limpiar filtros
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-neutral-500 p-8 text-center">...</div>
        ) : apps.length === 0 ? (
          <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-10 text-center text-neutral-500">
            No hay aplicaciones que coincidan con tus filtros.
          </div>
        ) : (
          <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] overflow-hidden">
            <div className="grid grid-cols-[1fr_120px_140px_140px] gap-3 px-4 py-3 border-b border-[#1a1a1a] text-[10px] uppercase tracking-widest text-neutral-500">
              <div>Jugadora</div>
              <div>Posición</div>
              <div>Club actual</div>
              <div>Estado</div>
            </div>
            {apps.map((a) => {
              const s = STATUSES.find((x) => x.key === a.status);
              return (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  data-testid={`elite-row-${a.id}`}
                  className="w-full grid grid-cols-[1fr_120px_140px_140px] gap-3 px-4 py-3 border-b border-[#111] hover:bg-[#111]/60 text-left transition-colors"
                >
                  <div>
                    <div className="font-medium text-white">{a.player_name}</div>
                    <div className="text-xs text-neutral-500">
                      {a.date_of_birth} · {a.age || "—"} yrs · {a.parent_name}
                    </div>
                  </div>
                  <div className="text-sm text-neutral-300">{a.primary_position}</div>
                  <div className="text-sm text-neutral-400 truncate">{a.current_club || "—"}</div>
                  <div>
                    <span className={`inline-flex px-2.5 py-1 text-[10px] uppercase tracking-widest border rounded-full ${s?.color || ""}`}>
                      {s?.label_es || a.status}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {selected && (
          <AppDetailModal
            app={selected}
            onClose={() => setSelected(null)}
            onUpdate={updateApp}
            onDelete={removeApp}
            onConvert={convertApp}
          />
        )}
      </main>
    </div>
  );
}

function AppDetailModal({ app, onClose, onUpdate, onDelete, onConvert }) {
  const [notes, setNotes] = useState(app.admin_notes || "");
  useEffect(() => setNotes(app.admin_notes || ""), [app.id, app.admin_notes]);
  const s = STATUSES.find((x) => x.key === app.status);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-2xl w-full max-w-3xl my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="elite-detail-modal"
      >
        <div className="flex items-start justify-between p-5 border-b border-[#1a1a1a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: GREEN }}>Aplicación · {app.id.slice(0, 8)}</div>
            <h2 className="font-bebas text-3xl tracking-widest mt-1">{app.player_name}</h2>
            <div className="text-xs text-neutral-500 mt-0.5">
              {app.date_of_birth} · {app.age || "—"} yrs · {app.primary_position}{app.secondary_position ? ` / ${app.secondary_position}` : ""} · foot: {app.preferred_foot}
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white text-2xl leading-none">✕</button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Club actual" value={app.current_club} />
          <Field label="Equipo" value={app.current_team} />
          <Field label="Nivel" value={app.competition_level} />
          <Field label="Años jugando" value={app.years_playing} />
          <Field label="Padre/tutor" value={app.parent_name} />
          <Field label="Email" value={app.parent_email} />
          <Field label="Teléfono" value={app.parent_phone} />
          {app.highlight_video_url && (
            <Field label="Video highlights" span={2}>
              <a href={app.highlight_video_url} target="_blank" rel="noreferrer" className="text-sm break-all hover:underline" style={{ color: GREEN }}>
                {app.highlight_video_url}
              </a>
            </Field>
          )}
          <Field label="¿Por qué Elite?" value={app.why_elite} span={2} />
          <Field label="Meta más grande" value={app.biggest_goal} span={2} />
          <Field label="Qué quiere mejorar" value={app.improve_most} span={2} />
        </div>

        {/* Status control */}
        <div className="px-5 pb-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Cambiar estado</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((st) => (
                <button
                  key={st.key}
                  onClick={() => onUpdate(app.id, { status: st.key })}
                  data-testid={`elite-set-status-${st.key}`}
                  className={`px-3 py-1.5 rounded-md text-[11px] uppercase tracking-widest border transition-colors ${
                    app.status === st.key ? st.color : "border-[#1a1a1a] text-neutral-400 hover:border-neutral-500"
                  }`}
                >
                  {st.label_es}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Notas internas</div>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => notes !== app.admin_notes && onUpdate(app.id, { admin_notes: notes })}
              data-testid="elite-admin-notes"
              className="w-full bg-[#050505] border border-[#1a1a1a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none resize-none"
              placeholder="Impresiones del coach, próximos pasos, etc."
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#1a1a1a] flex flex-wrap gap-2 justify-between">
          <button
            onClick={() => onDelete(app.id)}
            className="px-3 py-2 rounded-md bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm hover:bg-[#e05555]/20"
          >
            🗑 Eliminar
          </button>
          <div className="flex gap-2">
            <a
              href={`mailto:${app.parent_email}?subject=SoccerTroop Elite - ${app.player_name}`}
              className="px-3 py-2 rounded-md border border-[#1a1a1a] text-sm text-neutral-300 hover:border-neutral-500"
            >
              ✉ Responder al padre
            </a>
            {app.status === "accepted" && !app.converted_player_id && (
              <button
                onClick={() => onConvert(app)}
                data-testid="elite-convert-btn"
                className="px-4 py-2 rounded-md font-bebas tracking-widest text-sm text-black"
                style={{ backgroundColor: GREEN, boxShadow: `0 0 20px ${GREEN}40` }}
              >
                ★ Convertir en jugador
              </button>
            )}
            {app.converted_player_id && (
              <Link
                to={`/admin/player/${app.converted_player_id}`}
                className="px-4 py-2 rounded-md font-bebas tracking-widest text-sm border"
                style={{ color: GREEN, borderColor: `${GREEN}40` }}
              >
                Ver perfil jugadora →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, children, span = 1 }) {
  if (!value && !children) return null;
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className="text-sm text-white leading-relaxed whitespace-pre-wrap">{children || value}</div>
    </div>
  );
}
